import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { sendApiRequest } from '../../utils';
import { toast } from 'react-toastify';
import { FiPrinter, FiArrowLeft, FiX, FiCheck, FiChevronDown, FiSearch } from 'react-icons/fi';
import Loader from "../../pages/Loader";
import { AllPartnerStages } from '../../utils';

// Searchable Select Component
const SearchableSelect = ({ 
  options, 
  value, 
  onChange, 
  placeholder = 'Search...',
  renderOption = (option) => option.label,
  getOptionValue = (option) => option.value,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredOptions = options.filter(option => 
    renderOption(option).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOption = options.find(opt => getOptionValue(opt) === value);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div 
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 flex justify-between items-center cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="truncate">
          {selectedOption ? renderOption(selectedOption) : placeholder}
        </span>
        <FiChevronDown className={`h-5 w-5 text-gray-500 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
      </div>
      
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg">
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <FiSearch className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={placeholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-2 text-gray-500">No options found</div>
            ) : (
              filteredOptions.map((option) => (
                <div
                  key={getOptionValue(option)}
                  className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                    value === getOptionValue(option) ? 'bg-blue-50 text-blue-700' : ''
                  }`}
                  onClick={() => {
                    onChange(getOptionValue(option));
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                >
                  {renderOption(option)}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};


// Create a map from rank name to commission rate
const rankToCommission = AllPartnerStages.reduce((map, stage) => {
  const rate = parseFloat(stage.value.replace('%', '')) / 100;
  map[stage.name] = rate;
  return map;
}, {});

const getCommissionRate = (rank) => rankToCommission[rank] || 0;

const GenerateManuallyReceipt = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [packages, setPackages] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [mlmTree, setMlmTree] = useState(null);
  const [isLoadingMlm, setIsLoadingMlm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [existingReceipts, setExistingReceipts] = useState([]);
  const [selectedExistingReceipt, setSelectedExistingReceipt] = useState(null);

  const [receiptData, setReceiptData] = useState({
    receiptDate: new Date().toISOString().split('T')[0],
    packageId: '',
    customerId: '',
    partnerId: '',
    amount: '',
    paid_amount: '',
    amountInWords: '',
    paymentMode: 'cash',
    balance_amount: '',
    paymentDetails: '',
    remarks: '',
    mlmData: null,
  });

  const [distribution, setDistribution] = useState({
    totalAmount: 0,
    totalCommission: 0,
    remainingAmount: 0,
    distributionDetails: [],
  });

  const queryParams = new URLSearchParams(location.search);
  const requestId = queryParams.get('requestId');

  useEffect(() => {
    if (requestId) {
      fetchRequestDetails();
    }
  }, [requestId]);

  const fetchRequestDetails = async () => {
    try {
      const response = await sendApiRequest('api/packages/admin/all-requests', {}, 'GET');
      if (response.data.success) {
        const foundRequest = response.data.data.find((req) => req._id === requestId);
        if (foundRequest) {
          setReceiptData((prev) => ({
            ...prev,
            packageId: foundRequest?.package._id,
            customerId: foundRequest?.customer?._id,
            partnerId: foundRequest.user ? foundRequest.user._id : '',
            amount: foundRequest.package.price.toString(),
            amountInWords: numberToWords(foundRequest.package.price),
          }));
        }
      } else {
        toast.error(response.data.message || 'Failed to fetch package request details');
      }
    } catch (error) {
      console.error('Error fetching package request details:', error);
      toast.error('Failed to fetch package request details');
    }
  };

  const calculateMlmDistribution = (amount, mlmTree) => {
    const numericAmount = parseFloat(amount) || 0;
    const totalCap = 0.25; // 25% commission cap

    if (!mlmTree || !mlmTree.currentUser) {
      const commission = numericAmount * totalCap;
      const remaining = numericAmount - commission;
      return {
        totalAmount: numericAmount,
        totalCommission: commission,
        remainingAmount: remaining,
        distributionDetails: [{ label: 'Company Commission', member_id: "680a0046ffc5f2377494d8e1", value: commission }],
      };
    }

    // Get all members in the chain
    const allMembers = [mlmTree.currentUser, ...mlmTree.upline.ancestors];

    // Sort the chain by commission rate (lowest to highest)
    const sortedChain = allMembers.sort((a, b) => getCommissionRate(a.rank) - getCommissionRate(b.rank));

    let distributionDetails = [];
    let previousRate = 0;
    let totalCommission = 0;

    // Get the business originator's rate (current user's rate)
    const originatorRate = getCommissionRate(mlmTree.currentUser.rank);

    // Second pass to distribute commissions
    sortedChain.forEach((member) => {
      const rank = member.rank;
      const rate = getCommissionRate(rank);

      // Skip this member if their rate is lower than the business originator's rate
      if (rate < originatorRate) {
        return;
      }

      const commission = (rate - previousRate) * numericAmount;
      if (commission > 0) {
        distributionDetails.push({
          label: `${member.fullName} (${rank})`,
          member_id: member._id,
          value: commission,
        });
        totalCommission += commission;
      }
      previousRate = rate;
    });

    // If total commission is less than 25%, assign remainder to company
    if (previousRate < totalCap) {
      const remainingCommission = (totalCap - previousRate) * numericAmount;
      distributionDetails.push({
        label: 'Company',
        member_id: "680a0046ffc5f2377494d8e1",
        value: remainingCommission,
      });
      totalCommission += remainingCommission;
    }

    const remainingAmount = numericAmount - totalCommission;

    return {
      totalAmount: numericAmount,
      totalCommission,
      remainingAmount,
      distributionDetails,
    };
  };

  useEffect(() => {
    const amount = parseFloat(receiptData.amount) || 0;
    setDistribution(calculateMlmDistribution(amount, mlmTree));
  }, [receiptData.amount, mlmTree]);

  useEffect(() => {
    // Change from amount to paid_amount for commission calculation
    const paidAmount = parseFloat(receiptData.paid_amount) || 0;
    setDistribution(calculateMlmDistribution(paidAmount, mlmTree));
  }, [receiptData.paid_amount, mlmTree]); // Change dependency from amount to paid_amount

  useEffect(() => {
    fetchMlmTree(receiptData.partnerId);
  }, [receiptData.partnerId]);

  const fetchMlmTree = async (userId) => {
    if (!userId) {
      setMlmTree(null);
      return;
    }

    setIsLoadingMlm(true);
    try {
      const response = await sendApiRequest(`api/users/my-mlm-tree/${userId}`, {}, 'GET');
      if (response.data.success) {
        setMlmTree(response.data.data);
        setReceiptData((prev) => ({
          ...prev,
          mlmData: response.data.data,
        }));
      }
    } catch (error) {
      console.error('Error fetching MLM tree:', error);
    } finally {
      setIsLoadingMlm(false);
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const pkgResponse = await sendApiRequest('api/packages', {}, 'GET');
        setPackages(Array.isArray(pkgResponse.data) ? pkgResponse.data : []);

        const custResponse = await sendApiRequest('api/admin/customers', {}, 'GET');
        setCustomers(Array.isArray(custResponse.data.data) ? custResponse.data.data : []);

        const partnerResponse = await sendApiRequest('api/admin/partners', {}, 'GET');
        setPartners(Array.isArray(partnerResponse.data.data) ? partnerResponse.data.data : []);
      } catch (error) {
        console.error('Error fetching initial data:', error);
        toast.error('Failed to fetch initial data');
      }
    };

    fetchInitialData();
  }, []);

  // Convert number to words function
  const numberToWords = (num) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
      'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const scales = ['', 'Thousand', 'Lakh', 'Crore'];

    // Handle zero
    if (num === 0) return 'Zero Rupees';

    // Convert number to string and handle decimals
    const [rupees, paise] = num.toString().split('.');
    let rupeesInWords = '';
    let paiseInWords = '';

    // Function to convert a group of 2 digits
    const convertGroup = (n) => {
      if (n < 20) return ones[n];
      const digit1 = Math.floor(n / 10);
      const digit2 = n % 10;
      return tens[digit1] + (digit2 ? ' ' + ones[digit2] : '');
    };

    // Convert rupees
    const numStr = rupees.padStart(11, '0'); // pad to handle crores
    const groups = [];
    groups.push(parseInt(numStr.slice(-3))); // last 3 digits
    groups.push(parseInt(numStr.slice(-5, -3))); // thousands
    groups.push(parseInt(numStr.slice(-7, -5))); // lakhs
    groups.push(parseInt(numStr.slice(-11, -7))); // crores

    for (let i = 0; i < groups.length; i++) {
      if (groups[i] === 0) continue;

      if (i === 0) { // handling hundreds
        if (Math.floor(groups[i] / 100) > 0) {
          rupeesInWords = ones[Math.floor(groups[i] / 100)] + ' Hundred ' + rupeesInWords;
        }
        if (groups[i] % 100 > 0) {
          rupeesInWords += (rupeesInWords ? 'and ' : '') + convertGroup(groups[i] % 100);
        }
      } else {
        rupeesInWords = convertGroup(groups[i]) + ' ' + scales[i] + ' ' + rupeesInWords;
      }
    }

    // Convert paise
    if (paise) {
      const paiseNum = parseInt(paise.padEnd(2, '0').slice(0, 2));
      if (paiseNum > 0) {
        paiseInWords = convertGroup(paiseNum) + ' Paise';
      }
    }

    // Combine rupees and paise
    let result = rupeesInWords.trim() + ' Rupees';
    if (paiseInWords) {
      result += ' and ' + paiseInWords;
    }

    return result;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'customerId') {
      fetchCustomerReceipts(value);
    }

    setReceiptData((prev) => {
      const newData = {
        ...prev,
        [name]: value,
      };

      // Calculate balance amount when either total amount or paid amount changes
      if (name === 'amount' || name === 'paid_amount') {
        const totalAmount = parseFloat(newData.amount) || 0;
        const paidAmount = parseFloat(newData.paid_amount) || 0;
        newData.balance_amount = (totalAmount - paidAmount).toFixed(2);
      }

      // Update amount in words when paid amount changes
      if (name === 'paid_amount' && value) {
        const numericValue = parseFloat(value);
        if (!isNaN(numericValue)) {
          newData.amountInWords = numberToWords(numericValue);
        }
      }

      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault(); // Make e optional since we might call this directly
    setIsLoading(true);
    setIsPreviewOpen(false); // Close the modal when submitting

    const finalReceiptData = {
      ...receiptData,
      distribution: distribution,
      selectedExistingReceiptId: selectedExistingReceipt?._id
    };
    
    const existPayload = {
      ...finalReceiptData,
      receiptDate:new Date().toISOString()
    }
    // console.log(existPayload)



    try {
      const response = await sendApiRequest(
        'api/packages/admin/generate-receipt-manually',
        existPayload,
        'POST'
      );

      if (response.data.success) {
        toast.success('Receipt generated successfully');
        navigate(`/admin/packages/view-receipt?receiptId=${response.data.data.mainReceiptId}`);
      } else {
        toast.error(response.data.message || 'Failed to generate receipt');
      }
    } catch (error) {
      console.error('Error generating receipt:', error);
      toast.error('Failed to generate receipt');
    }
    setIsLoading(false);
  };

  const handlePrint = () => {
    window.print();
  };

  const handlePreview = (e) => {
    e.preventDefault();
    // Set preview data with current form values and calculated distribution
    setPreviewData({
      ...receiptData,
      distribution: calculateMlmDistribution(receiptData.paid_amount, mlmTree)
    });
    setIsPreviewOpen(true);
  };

  const handleBack = () => {
    navigate('/admin/packages/approval');
  };

  const fetchCustomerReceipts = async (customerId) => {
    if (!customerId) {
      setExistingReceipts([]);
      setSelectedExistingReceipt(null);
      return;
    }

    try {
      const response = await sendApiRequest(`api/packages/customer-receipts/${customerId}`, {}, 'GET');
      if (response.data.success) {
        setExistingReceipts(response.data.data);
      } else {
        toast.error(response.data.message || 'Failed to fetch customer receipts');
      }
    } catch (error) {
      console.error('Error fetching customer receipts:', error);
      toast.error('Failed to fetch customer receipts');
    }
  };

  const handleExistingReceiptSelect = (receiptId) => {
    const receipt = existingReceipts.find(r => r._id === receiptId);
    if (receipt) {
      setSelectedExistingReceipt(receipt);
      setReceiptData(prev => ({
        ...prev,
        packageId: receipt.package._id,
        amount: receipt.balance_amount.toString(),
        amountInWords: numberToWords(receipt.balance_amount),
        paid_amount: '',
        balance_amount: receipt.balance_amount.toString(),
        remarks: ``
      }));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#49c2c7]/5 via-[#25b2da]/10 to-[#0d61b5]/5 py-8">
      {isPreviewOpen && (
        <div className="fixed inset-0 bg-gradient-to-br from-gray-900/50 to-gray-800/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold text-gray-800">Receipt Preview</h2>
              <button
                onClick={() => setIsPreviewOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <FiX className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-700 mb-2">Basic Details</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Receipt Date:</span>
                      <span className="font-medium text-gray-900">{previewData?.receiptDate}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Package:</span>
                      <span className="font-medium text-gray-900">{packages.find(p => p._id === previewData?.packageId)?.title}</span>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-700 mb-2">Customer & Partner</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Customer:</span>
                      <span className="font-medium text-gray-900">{customers.find(c => c._id === previewData?.customerId)?.customerName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Partner:</span>
                      <span className="font-medium text-gray-900">{partners.find(p => p._id === previewData?.partnerId)?.fullName}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-4">Financial Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex flex-col">
                    <span className="text-gray-600">Total Amount</span>
                    <span className="text-2xl font-bold text-blue-600">₹{previewData?.amount}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-gray-600">Paid Amount</span>
                    <span className="text-2xl font-bold text-green-600">₹{previewData?.paid_amount}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-gray-600">Balance</span>
                    <span className="text-2xl font-bold text-red-600">₹{previewData?.balance_amount}</span>
                  </div>
                </div>
                <div className="mt-4">
                  <span className="text-gray-600">Amount in Words</span>
                  <p className="mt-1 text-gray-900">{previewData?.amountInWords}</p>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-4">Payment Information</h3>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Payment Mode</span>
                    <span className="font-medium text-gray-900">{previewData?.paymentMode}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Payment Details</span>
                    <span className="font-medium text-gray-900">{previewData?.paymentDetails || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Remarks</span>
                    <span className="font-medium text-gray-900">{previewData?.remarks || 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-4">Commission Distribution</h3>
                <div className="space-y-4">
                  {previewData?.distribution?.distributionDetails.map((item, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <span className="text-gray-600">{item.label}</span>
                      <span className="font-medium text-green-600">₹{item.value.toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex justify-between items-center font-semibold">
                      <span>Total Commission</span>
                      <span className="text-2xl text-green-600">₹{previewData?.distribution?.totalCommission.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-end space-x-4">
              <button
                onClick={() => setIsPreviewOpen(false)}
                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center space-x-2"
              >
                <FiX className="w-5 h-5" />
                <span>Close Preview</span>
              </button>
              <button
                onClick={handleSubmit}
                disabled={isLoading}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all flex items-center space-x-2"
              >
                {isLoading ? (
                  <>
                    <Loader className="w-5 h-5" />
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <FiCheck className="w-5 h-5" />
                    <span>Confirm & Generate</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="container mx-auto px-4">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-[#25b2da]/20">
          <div className="bg-gradient-to-r from-[#13407e] to-[#0d61b5] text-white px-6 py-4 flex justify-between items-center">
            <h1 className="text-xl font-semibold">Generate Package Receipt</h1>
            <div className="flex space-x-2">
              <button
                onClick={handleBack}
                className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
              >
                <FiArrowLeft className="w-5 h-5" />
              </button>
              <button
                onClick={handlePrint}
                className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
              >
                <FiPrinter className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="p-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Receipt Date</label>
                <input
                  type="date"
                  name="receiptDate"
                  value={receiptData.receiptDate}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Package</label>
                <SearchableSelect
                  options={packages.map(pkg => ({
                    ...pkg,
                    label: `${pkg.title} - ₹${pkg.price} - ${pkg.width} * ${pkg.length}`,
                    value: pkg._id
                  }))}
                  value={receiptData.packageId}
                  onChange={(value) => handleInputChange({ target: { name: 'packageId', value } })}
                  placeholder="Search packages..."
                />
              </div>

              {/* Move Existing Customer Packages section here */}
              {existingReceipts.length > 0 && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h3 className="text-lg font-medium text-blue-800 mb-3">Existing Customer Packages</h3>
                  <SearchableSelect
                    options={existingReceipts.map(receipt => ({
                      ...receipt,
                      label: `${receipt.package.title} - ₹${receipt.package.price} - ${receipt.package.width} * ${receipt.package.length} (Balance: ₹${receipt.balance_amount})`,
                      value: receipt._id
                    }))}
                    value={selectedExistingReceipt?._id || ''}
                    onChange={(value) => handleExistingReceiptSelect(value)}
                    placeholder="Search existing packages..."
                  />
                </div>
              )}

              {/* Add a note about partial payment if an existing receipt is selected */}
              {selectedExistingReceipt && (
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <p className="text-yellow-800">
                    <strong>Note:</strong> This is a partial payment for receipt {selectedExistingReceipt.receiptNumber}.
                    The remaining balance is ₹{selectedExistingReceipt.balance_amount}.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Customer</label>
                <SearchableSelect
                  options={customers.map(customer => ({
                    ...customer,
                    label: `${customer.customerName} - ${customer.customerPhone}`,
                    value: customer._id
                  }))}
                  value={receiptData.customerId}
                  onChange={(value) => handleInputChange({ target: { name: 'customerId', value } })}
                  placeholder="Search customers..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Partner</label>
                <SearchableSelect
                  options={partners.map(partner => ({
                    ...partner,
                    label: `${partner.fullName} (${partner.sponsorCode})`,
                    value: partner._id
                  }))}
                  value={receiptData.partnerId}
                  onChange={(value) => handleInputChange({ target: { name: 'partnerId', value } })}
                  placeholder="Search partners..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Payable Amount</label>
                <input
                  type="number"
                  name="amount"
                  value={receiptData.amount}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Paid Amount</label>
                <input
                  type="number"
                  name="paid_amount"
                  value={receiptData.paid_amount}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Balance Amount</label>
                <input
                  type="number"
                  name="balance_amount"
                  value={receiptData.balance_amount}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  readOnly
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Paid Amount in Words</label>
                <input
                  type="text"
                  name="amountInWords"
                  value={receiptData.amountInWords}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  readOnly
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Mode</label>
                <select
                  name="paymentMode"
                  value={receiptData.paymentMode}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="cash">Cash</option>
                  <option value="bank">Bank</option>
                  <option value="upi">UPI</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Details</label>
                <input
                  type="text"
                  name="paymentDetails"
                  value={receiptData.paymentDetails}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Transaction ID, Cheque Number"
                />
              </div>

              {/* Amount Distribution Section */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h3 className="text-lg font-medium text-gray-800 mb-3">Amount Distribution</h3>
                <div className="space-y-2">
                  {distribution.distributionDetails.map((item, index) => (
                    <div key={index} className="flex justify-between">
                      <span className="text-gray-700">{item.label}:</span>
                      <span className="font-medium">₹{item.value.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex justify-between font-medium">
                    <span>Total Commission:</span>
                    <span>₹{distribution.totalCommission.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-medium">
                    <span>Remaining Amount:</span>
                    <span>₹{distribution.remainingAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-medium">
                    <span>Total Amount:</span>
                    <span>₹{distribution.totalAmount.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* MLM Information Section */}
              {mlmTree && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-medium text-gray-800 mb-3">MLM Information</h3>
                  {isLoadingMlm ? (
                    <p>Loading MLM data...</p>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <span className="font-medium text-gray-700">Current Partner:</span>
                        <span className="ml-2 text-gray-600">
                          {mlmTree.currentUser.fullName} ({mlmTree.currentUser.sponsorCode}) -{' '}
                          {mlmTree.currentUser.rank}
                        </span>
                      </div>
                      {mlmTree.upline?.ancestors?.length > 0 && (
                        <div>
                          <span className="font-medium text-gray-700">Upline Hierarchy:</span>
                          {mlmTree.upline.ancestors.map((ancestor, index) => (
                            <div key={ancestor._id} className="flex items-center space-x-2 mt-1">
                              <span className="text-gray-500">Level {index + 1}:</span>
                              <span className="text-gray-600">
                                {ancestor.fullName} ({ancestor.sponsorCode}) - {ancestor.rank}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                <textarea
                  name="remarks"
                  value={receiptData.remarks}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end">
                <div className="flex justify-end">
                  <button
                    onClick={handlePreview}  // Changed from handleSubmit to handlePreview
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Preview Receipt
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenerateManuallyReceipt;